import {
  api,
  type FilingStatus,
  type ProcessingViewResponse,
  type RoomDocument,
} from '@dataroom/shared';

/**
 * The admin's PROCESSING list (owner, 2026-10-15): "Anything listed in
 * 'processing status' view should be something that is processing … Just items
 * that have completed everything, either with assistance or without, should be
 * removed from the list." And: "Once I leave the 'Add documents' view … I lose
 * the 'processing status' indicator forever" — so the status comes from SERVER
 * state only (GET /rooms/:id/processing), never from the upload panel.
 *
 * A document's stages and where each one ends:
 *   filing     classifying → filed | not_filed (deal paperwork) — finished;
 *              needs_filing | failed (couldn't file) — a person files it;
 *              (the date / period tags are set by the same classification)
 *   OCR        pending → complete | none — finished; failed — a person looks;
 *   redaction  pending (queued, detecting, applying, waiting for OCR) →
 *              complete (verified copy ready, or nothing needed hiding) | off —
 *              finished; needs_review | failed | unsupported — a person acts.
 * A document stays listed until EVERY stage is finished; the server buckets it
 * (`unfinished` = `in_progress` ∪ `needs_attention`, processing-view.ts).
 */
export type ProcessingQueueFilter = 'unfinished' | 'needs_attention';

/** The listing's counts, with the admin list's buckets (absent from an older server). */
export type ProcessingQueueCounts = ProcessingViewResponse['counts'] &
  Partial<Record<'in_progress' | 'needs_attention' | 'unfinished', number>>;

export interface ProcessingQueueResponse extends Omit<ProcessingViewResponse, 'counts'> {
  counts: ProcessingQueueCounts;
}

export const PROCESSING_QUEUE_PAGE_SIZE = 50;

export function processingQueueKey(roomId: string, filter: ProcessingQueueFilter, page: number) {
  return ['rooms', roomId, 'processing', 'queue', filter, page];
}

/** Newest uploads first, the whole room (no upload-day range). */
/** The list is read over the whole room, so it can be slow while a big batch runs; but a read that never comes back must end as an error with "Try again", never as "Loading…" for ever. */
export const PROCESSING_QUEUE_TIMEOUT_MS = 45_000;

export function fetchProcessingQueue(
  roomId: string,
  filter: ProcessingQueueFilter,
  page: number,
  signal?: AbortSignal,
): Promise<ProcessingQueueResponse> {
  const q = new URLSearchParams({
    status: filter,
    page: String(page),
    pageSize: String(PROCESSING_QUEUE_PAGE_SIZE),
    order: 'desc',
  });
  const timeout = AbortSignal.timeout(PROCESSING_QUEUE_TIMEOUT_MS);
  return api.get<ProcessingQueueResponse>(`/rooms/${roomId}/processing?${q.toString()}`, {
    signal:
      signal && typeof AbortSignal.any === 'function'
        ? AbortSignal.any([signal, timeout])
        : timeout,
  });
}

export interface ProcessingSummary {
  /** Every document in the room. */
  all: number;
  /** Not finished every stage (running, or waiting on a person). */
  unfinished: number;
  /** all − unfinished. */
  finished: number;
  /** A stage queued or running. */
  inProgress: number;
  /** A stage waiting on a person. */
  needsAttention: number;
  classifying: number;
  ocr: number;
  redacting: number;
}

export function processingSummary(counts: ProcessingQueueCounts): ProcessingSummary {
  const unfinished = counts.unfinished ?? 0;
  return {
    all: counts.all,
    unfinished,
    finished: Math.max(0, counts.all - unfinished),
    inProgress: counts.in_progress ?? 0,
    needsAttention: counts.needs_attention ?? 0,
    classifying: counts.classifying,
    ocr: counts.ocr_processing,
    redacting: counts.redaction_processing,
  };
}

/** "Processing 87 of 214 · Classifying 20 · OCR 12 · Redacting 55 · Needs you 5". */
export function processingHeadline(s: ProcessingSummary): string {
  const parts = [`Processing ${s.unfinished} of ${s.all}`];
  if (s.classifying > 0) parts.push(`Classifying ${s.classifying}`);
  if (s.ocr > 0) parts.push(`OCR ${s.ocr}`);
  if (s.redacting > 0) parts.push(`Redacting ${s.redacting}`);
  if (s.needsAttention > 0) parts.push(`Needs you ${s.needsAttention}`);
  return parts.join(' · ');
}

/** What a person must do for a document, or null when nothing waits on one. */
export type AttentionKind = 'filing' | 'redaction' | 'ocr';

export interface Attention {
  kind: AttentionKind;
  /** The in-row chip, e.g. "Needs you: file it". */
  label: string;
  /** The action link's words. */
  action: string;
}

/** The document's filing status, or one read off its classification (mocks, older servers). */
function filingOf(
  d: Pick<RoomDocument, 'filingStatus' | 'classificationStatus'>,
): FilingStatus | null {
  if (d.filingStatus) return d.filingStatus;
  switch (d.classificationStatus) {
    case 'processing':
      return 'classifying';
    case 'classified':
      return 'filed';
    case 'low_confidence':
      return 'needs_filing';
    case 'failed':
      return 'failed';
    case 'not_classifiable':
      return 'not_filed';
    default:
      return null;
  }
}

type StageFields = Pick<
  RoomDocument,
  'filingStatus' | 'classificationStatus' | 'ocrStatus' | 'redactionStatus'
>;

/** Which stage waits on a person (filing first: redaction waits for it). */
export function attentionOf(d: StageFields): Attention | null {
  const filing = filingOf(d);
  if (filing === 'needs_filing' || filing === 'failed') {
    return { kind: 'filing', label: 'Needs you: file it', action: 'Review filing' };
  }
  if (
    d.redactionStatus === 'needs_review' ||
    d.redactionStatus === 'failed' ||
    d.redactionStatus === 'unsupported'
  ) {
    return { kind: 'redaction', label: 'Needs you: review redaction', action: 'Review redaction' };
  }
  if (d.ocrStatus === 'failed') {
    return { kind: 'ocr', label: 'Needs you: OCR failed', action: 'View' };
  }
  return null;
}

/** A stage still queued or running. */
export function isStageRunning(d: StageFields): boolean {
  return (
    filingOf(d) === 'classifying' || d.ocrStatus === 'pending' || d.redactionStatus === 'pending'
  );
}

/** Not finished every stage — the client mirror of the server's `unfinished` bucket. */
export function isUnfinished(d: StageFields): boolean {
  return isStageRunning(d) || attentionOf(d) !== null;
}

/** Where "Review redaction" goes: the redaction review screen, at one document. */
export { redactionReviewPath } from './redaction-review';

/** The filing review queue, at one document. */
export function filingReviewPath(roomId: string, documentId: string): string {
  return `/rooms/${roomId}/review?doc=${encodeURIComponent(documentId)}`;
}
